import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { canAll, primaryRole } from '../auth/rbac';
import { PERMISSIONS } from '../auth/permissions';
import { api } from '../api/client';
import { MsaAcceptanceModal } from './MsaAcceptanceModal';
import { ReferenceIcon, type ReferenceIconName } from './ReferenceIcon';
import type { GlobalSearchResultKind, Permission, Role, Session } from '../types';

type NavItem = {
  to: string;
  label: string;
  icon: ReferenceIconName;
  hidden?: boolean;
  permissions?: Permission[];
  roles?: Role[];
};
type NavGroup = { id: string; label: string; items: NavItem[]; collapsible?: boolean };
type SearchItem = {
  key: string;
  source: 'module' | 'backend';
  title: string;
  subtitle: string;
  url: string;
  icon: ReferenceIconName;
  status?: string;
};

const searchKindMeta: Record<GlobalSearchResultKind, { label: string; icon: ReferenceIconName }> = {
  uploaded_data: { label: 'Uploaded data', icon: 'database' },
  submission: { label: 'Catalog submission', icon: 'file' },
  client_catalog: { label: 'Client catalog', icon: 'grid' },
  user: { label: 'User', icon: 'user' },
  client: { label: 'Client', icon: 'building' },
  vault_dataset: { label: 'Vault dataset', icon: 'shield' },
  connection: { label: 'API connection', icon: 'plug' },
  retail: { label: 'Retail record', icon: 'archive' },
};

const navGroups: NavGroup[] = [
  {
    id: 'admin',
    label: '',
    items: [
      { to: '/', label: 'Dashboard', icon: 'grid', permissions: [PERMISSIONS.DASHBOARD_READ, PERMISSIONS.JOB_READ] },
      { to: '/freelancer/portal', label: 'Data Acquisition Portal', icon: 'upload', roles: ['freelancer'], permissions: [PERMISSIONS.SUBMISSION_READ_MINE] },
      { to: '/uploads', label: 'Upload Data', icon: 'upload', permissions: [PERMISSIONS.DATASET_CREATE] },
      { to: '/processing', label: 'Uploaded Data', icon: 'database', permissions: [PERMISSIONS.JOB_READ] },
      { to: '/explore-datasets', label: 'Explore Datasets', icon: 'search', permissions: [PERMISSIONS.CLIENT_CATALOG_READ] },
      { to: '/acquisition/qc-review', label: 'QC Workbench', icon: 'upload', roles: ['qc', 'superadmin'], permissions: [PERMISSIONS.SUBMISSION_QC_REVIEW] },
      { to: '/acquisition/superadmin', label: 'Freelancer Files', icon: 'users', roles: ['superadmin'], permissions: [PERMISSIONS.SUBMISSION_SUPERADMIN_READ] },
      { to: '/acquisition/client-files', label: 'Client Files', icon: 'building', roles: ['superadmin'], permissions: [PERMISSIONS.SUBMISSION_SUPERADMIN_READ] },
      { to: '/retail-datasets', label: 'Retail Datasets', icon: 'archive', hidden: true, permissions: [PERMISSIONS.VAULT_READ, PERMISSIONS.SUBMISSION_QC_REVIEW] },
    ],
  },
  {
    id: 'access',
    label: 'USER MANAGEMENT',
    collapsible: true,
    items: [
      { to: '/users', label: 'User List', icon: 'user', roles: ['superadmin', 'admin', 'manager'], permissions: [PERMISSIONS.USER_READ] },
      { to: '/clients', label: 'Client List', icon: 'building', roles: ['superadmin'], permissions: [PERMISSIONS.USER_READ] },
    ],
  },
];

const utilityItems: NavItem[] = [
  { to: '/data-sources', label: 'API Connections', icon: 'plug', permissions: [PERMISSIONS.SOURCE_READ] },
  { to: '/vault-datasets', label: 'Protected Data', icon: 'database', permissions: [PERMISSIONS.VAULT_READ] },
  { to: '/user-access', label: 'User access', icon: 'users', roles: ['superadmin', 'admin', 'manager'], permissions: [PERMISSIONS.USER_READ, PERMISSIONS.USER_UPDATE] },
  { to: '/permissions', label: 'Permissions', icon: 'lock', roles: ['superadmin', 'admin'], permissions: [PERMISSIONS.PERMISSION_READ] },
  { to: '/profile', label: 'Profile', icon: 'user' },
  { to: '/settings/account', label: 'Settings', icon: 'settings' },
  { to: '/settings/security', label: 'Security settings', icon: 'shield' },
];

const titles: Record<string, [string, string, string]> = {
  '/': ['Dashboard', 'Monitor media protection activity across your workspace', ''],
  '/freelancer/portal': ['Data Acquisition Portal', 'Securely upload, version, and manage dataset contributions with rich AI model metadata', 'Workspace'],
  '/uploads': ['Upload Data', 'Securely upload source files and retrieve protected artifacts', 'Secure ingestion'],
  '/processing': ['Uploaded Data', 'Track queued, active, and completed protection work', 'Protected data'],
  '/explore-datasets': ['Explore Datasets', 'Discover curated, privacy-safe datasets available to your organization', 'Data Catalog'],
  '/data-sources': ['Upload Data', 'Connect sources (greytHR, APIs, storage) and configure automated vault syncs', 'Secure ingestion'],
  '/vault-datasets': ['Protected Data', 'Query protected records, manage field schemas, and perform authorized break-glass reveals', 'Data Vault'],
  '/retail-datasets': ['Retail Datasets', 'End-to-end labeled retail dataset factory: ingest, PII sanitization, coverage sampling, 3-tier QC, and packaging', 'Data Vault'],
  '/retail-datasets/pipeline': ['Retail Datasets', 'Advanced API-backed retail lifecycle workspace', 'Retail Dataset'],
  '/acquisition/superadmin': ['Freelancer Files', 'Superadmin intake, reviewer assignment, rights clearance & publication', 'Data Vault'],
  '/acquisition/client-files': ['Client Files', 'Review client-provided files and coordinate QC delivery', 'Data Vault'],
  '/acquisition/qc-review': ['QC Workbench', 'Inspect dataset files, evaluate against rubrics, and submit QC verdicts', 'Data Vault'],
  '/users': ['User List', 'Review workspace identities and effective access', 'User Management'],
  '/clients': ['Client List', 'Review client workspaces and active user totals', 'User Management'],
  '/user-access': ['User access', 'Manage roles, status, and account access', 'User Management'],
  '/user-access/new': ['Add user', 'Create an account and assign authorized workspace access', 'User Management'],
  '/user-access/new-client': ['Add client', 'Provision a client workspace and administrator account', 'User Management'],
  '/permissions': ['Permissions', 'Configure the permissions assigned to each role', 'Access Control'],
  '/profile': ['Your profile', 'Review identity, role, and workspace access', 'Settings'],
  '/settings/account': ['Account settings', 'Review account and workspace details', 'Settings'],
  '/settings/security': ['Security settings', 'Manage session and account security', 'Settings'],
};

export function AppShell({ children, session, onLogout }: { children: ReactNode; session: Session; onLogout: () => Promise<void> }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [backendSearchResults, setBackendSearchResults] = useState<SearchItem[]>([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const globalSearchInputRef = useRef<HTMLInputElement>(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showMsaModal, setShowMsaModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isFreelancerWorkspace = Boolean(session.isFreelancerWorkspace || session.roles?.some((r) => r.toLowerCase() === 'freelancer'));
  const isSuperadmin = Boolean(session.roles?.some((role) => role.toLowerCase() === 'superadmin'));
  const isQc = Boolean(session.roles?.some((role) => role.toLowerCase() === 'qc'));
  const isRetailWorkspace = Boolean(
    session.isRetailWorkspace
    || isSuperadmin
    || isQc
    || session.permissions?.some((permissionKey) => permissionKey.startsWith('retail:')),
  );
  const isRetailClient = isRetailWorkspace && !isSuperadmin && !isQc;

  useEffect(() => {
    if (isFreelancerWorkspace) {
      api.acquisitionAgreementStatus()
        .then((res) => {
          if (!res.hasAcceptedActiveMsa) {
            setShowMsaModal(true);
          }
        })
        .catch((err) => {
          console.error('Failed to check MSA status:', err);
        });
    }
  }, [isFreelancerWorkspace]);

  const allowedGroups = useMemo(() => navGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (isRetailClient && group.id === 'admin' && item.to !== '/retail-datasets') return false;
      if (item.to === '/retail-datasets') return isRetailWorkspace;
      if (item.roles && !item.roles.some((r) => session.roles?.includes(r))) {
        return false;
      }
      return !item.permissions || canAll(session, item.permissions);
    }).map((item) => item.to === '/retail-datasets' ? { ...item, hidden: !isRetailClient } : item),
  })).filter((group) => group.items.length), [isRetailClient, isRetailWorkspace, session]);
  const allowedUtilityItems = useMemo(() => utilityItems.filter((item) => {
    if (isRetailClient && ['/data-sources', '/vault-datasets', '/permissions'].includes(item.to)) return false;
    if (item.roles && !item.roles.some((role) => session.roles?.includes(role))) return false;
    return !item.permissions || canAll(session, item.permissions);
  }), [isRetailClient, session]);

  const activeGroup = allowedGroups.find((group) => group.items.some((item) => routeMatches(location.pathname, item.to)))?.id;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => Object.fromEntries(navGroups.map((group) => [group.id, true])));
  const [title, , category] = titles[location.pathname] ?? ['Workspace', 'Secure workspace console', 'Workspace'];
  const topbarTitle = ['/users', '/clients'].includes(location.pathname) ? category : title;

  useEffect(() => {
    let active = true;
    api.acquisitionNotifications()
      .then((items) => { if (active) setUnreadNotifications(items.filter((item) => !item.readAt).length); })
      .catch(() => { if (active) setUnreadNotifications(0); });
    return () => { active = false; };
  }, [session.userId]);

  useEffect(() => {
    if (activeGroup) setExpanded((current) => ({ ...current, [activeGroup]: true }));
  }, [activeGroup]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileOpen]);

  const logout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try { await onLogout(); }
    finally { setSigningOut(false); }
  };

  const userRole = friendlyRole(primaryRole(session));
  const profileDisplayName = isSuperadmin ? 'Data Vault Admin' : session.displayName;
  const notificationTarget = session.roles?.includes('freelancer')
    ? '/freelancer/portal'
    : session.roles?.includes('superadmin')
      ? '/acquisition/superadmin'
      : session.roles?.includes('qc')
        ? '/acquisition/qc-review'
        : isRetailClient
          ? '/retail-datasets'
          : '/processing';
  const groupedSearchItems = allowedGroups.flatMap((group) => group.items);
  const groupedSearchPaths = new Set(groupedSearchItems.map((item) => item.to));
  const searchItems = [...groupedSearchItems, ...allowedUtilityItems.filter((item) => !groupedSearchPaths.has(item.to))];
  const normalizedGlobalSearch = globalSearch.trim().toLocaleLowerCase();
  const moduleSearchResults: SearchItem[] = (() => {
    if (!normalizedGlobalSearch) return [];
    return searchItems
      .filter((item) => {
        const routeTitle = titles[item.to] ?? ['', '', ''];
        return [item.label, item.to, ...routeTitle]
          .join(' ')
          .toLocaleLowerCase()
          .includes(normalizedGlobalSearch);
      })
      .slice(0, 8)
      .map((item) => ({
        key: `module:${item.to}`,
        source: 'module',
        title: item.label,
        subtitle: titles[item.to]?.[1] || `Open ${item.label}`,
        url: item.to,
        icon: item.icon,
      }));
  })();
  const visibleSearchResults = [...moduleSearchResults, ...backendSearchResults];

  useEffect(() => {
    const query = globalSearch.trim();
    if (query.length < 2) {
      setBackendSearchResults([]);
      setGlobalSearchLoading(false);
      setGlobalSearchError('');
      return;
    }
    setBackendSearchResults([]);
    setGlobalSearchLoading(true);
    let active = true;
    const timeout = window.setTimeout(() => {
      setGlobalSearchError('');
      api.globalSearch(query)
        .then((response) => {
          if (!active) return;
          setBackendSearchResults(response.results.map((result) => ({
            key: `backend:${result.kind}:${result.id}`,
            source: 'backend',
            title: result.title,
            subtitle: `${searchKindMeta[result.kind].label} · ${result.subtitle}`,
            url: result.url,
            icon: searchKindMeta[result.kind].icon,
            status: result.status ?? undefined,
          })));
        })
        .catch((cause) => {
          if (!active) return;
          setBackendSearchResults([]);
          setGlobalSearchError(cause instanceof Error ? cause.message : 'Search results are unavailable.');
        })
        .finally(() => { if (active) setGlobalSearchLoading(false); });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [globalSearch]);

  useEffect(() => {
    setActiveSearchIndex(0);
    if (normalizedGlobalSearch) setGlobalSearchOpen(true);
  }, [normalizedGlobalSearch]);

  useEffect(() => {
    const focusGlobalSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        globalSearchInputRef.current?.focus();
        if (globalSearch.trim()) setGlobalSearchOpen(true);
      }
      if (event.key === 'Escape' && globalSearchOpen) {
        setGlobalSearchOpen(false);
        globalSearchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', focusGlobalSearch);
    return () => window.removeEventListener('keydown', focusGlobalSearch);
  }, [globalSearch, globalSearchOpen]);

  const selectSearchItem = (item: SearchItem) => {
    navigate(item.url);
    setGlobalSearch('');
    setBackendSearchResults([]);
    setGlobalSearchOpen(false);
  };
  const submitGlobalSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const match = visibleSearchResults[activeSearchIndex] ?? visibleSearchResults[0];
    if (match) selectSearchItem(match);
  };

  return (
    <div className={`app-shell enterprise-theme ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      {showMsaModal && (
        <MsaAcceptanceModal
          onAccepted={() => {
            setShowMsaModal(false);
            window.dispatchEvent(new CustomEvent('msa-accepted', { detail: { hasAccepted: true } }));
            window.dispatchEvent(new CustomEvent('agreement-status-changed', { detail: { hasAccepted: true } }));
          }}
          onCancel={() => {
            setShowMsaModal(false);
          }}
        />
      )}
      {/* Top Navigation Bar */}
      <header className="topbar" role="banner">
        <div className="topbar-left">
          <button className="menu-button" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <ReferenceIcon name="menu" />
          </button>
          {sidebarCollapsed && (
            <button
              type="button"
              className="sidebar-toggle-btn"
              onClick={() => setSidebarCollapsed(false)}
              title="Expand sidebar"
              aria-label="Expand navigation"
            >
              <ReferenceIcon name="chevron" />
            </button>
          )}
          <div className="topbar-breadcrumbs" aria-label="Breadcrumb">
            <span>{category || 'Admin Panel'}</span>
            <ReferenceIcon name="chevron" />
            <strong>{topbarTitle}</strong>
          </div>
        </div>

        <div className="topbar-center">
          <form
            className="topbar-search"
            role="search"
            onSubmit={submitGlobalSearch}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setGlobalSearchOpen(false);
            }}
          >
            <ReferenceIcon name="search" className="search-icon" />
            <input
              ref={globalSearchInputRef}
              type="text"
              placeholder="Search anything"
              aria-label="Search the platform"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="global-search-results"
              aria-expanded={globalSearchOpen && Boolean(normalizedGlobalSearch)}
              aria-activedescendant={visibleSearchResults[activeSearchIndex] ? `global-search-result-${activeSearchIndex}` : undefined}
              value={globalSearch}
              onFocus={() => { if (normalizedGlobalSearch) setGlobalSearchOpen(true); }}
              onChange={(event) => setGlobalSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' && visibleSearchResults.length) {
                  event.preventDefault();
                  setActiveSearchIndex((index) => (index + 1) % visibleSearchResults.length);
                } else if (event.key === 'ArrowUp' && visibleSearchResults.length) {
                  event.preventDefault();
                  setActiveSearchIndex((index) => (index - 1 + visibleSearchResults.length) % visibleSearchResults.length);
                } else if (event.key === 'Escape') {
                  setGlobalSearchOpen(false);
                }
              }}
            />
            <kbd>⌘ K</kbd>
            {globalSearchOpen && normalizedGlobalSearch && <div className="global-search-panel" id="global-search-results" role="listbox" aria-label="Global search results">
              {moduleSearchResults.length > 0 && <div className="global-search-section-label">Platform</div>}
              {moduleSearchResults.map((item, index) => <GlobalSearchOption
                item={item}
                index={index}
                active={activeSearchIndex === index}
                onSelect={selectSearchItem}
                onHover={setActiveSearchIndex}
                key={item.key}
              />)}
              {backendSearchResults.length > 0 && <div className="global-search-section-label">Workspace data</div>}
              {backendSearchResults.map((item, backendIndex) => {
                const index = moduleSearchResults.length + backendIndex;
                return <GlobalSearchOption
                  item={item}
                  index={index}
                  active={activeSearchIndex === index}
                  onSelect={selectSearchItem}
                  onHover={setActiveSearchIndex}
                  key={item.key}
                />;
              })}
              {globalSearchLoading && <div className="global-search-state"><ReferenceIcon name="refresh" className="spin"/>Searching workspace data…</div>}
              {!globalSearchLoading && globalSearchError && <div className="global-search-state error" role="alert">{globalSearchError}</div>}
              {!globalSearchLoading && !globalSearchError && visibleSearchResults.length === 0 && <div className="global-search-state">No authorized modules or workspace records match “{globalSearch.trim()}”.</div>}
              {visibleSearchResults.length > 0 && <div className="global-search-help"><span>↑↓ Navigate</span><span>Enter Open</span><span>Esc Close</span></div>}
            </div>}
          </form>
        </div>

        <div className="topbar-right">
          <Link to={notificationTarget} className="topbar-icon-btn" aria-label={`${unreadNotifications} unread notifications`} title="Notifications">
            <ReferenceIcon name="bell" />
            <span className="notification-dot" aria-hidden="true" />
          </Link>

          <Link to="/settings/security" className="topbar-help" title="Security and account help"><ReferenceIcon name="help" />Help</Link>

          {/* Preserved accessible buttons for test compatibility */}
          <button className="logout-button sr-only" disabled={signingOut} onClick={() => void logout()} title="Sign out" aria-label="Sign out"><ReferenceIcon name="logout" /></button>
        </div>
      </header>

      <div className="layout-body">
        {/* Mobile Scrim */}
        {mobileOpen && <button className="nav-scrim" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}

        {/* Left Vertical Sidebar - Reference UI */}
        <aside className={`sidebar ${mobileOpen ? 'is-open' : ''} ${sidebarCollapsed ? 'is-collapsed' : ''}`} aria-label="Sidebar navigation">
          {/* Sidebar Top Brand Header with Logo & Collapse Toggle */}
          <div className="sidebar-brand-header">
            <Link to="/" className="sidebar-brand" aria-label="Data Privacy Factory">
              <div className="sidebar-brand-mark">
                <ReferenceIcon name="shield" />
              </div>
              <div className="sidebar-brand-text">
                <strong>Data Privacy</strong>
                <span>FACTORY</span>
              </div>
            </Link>

            <button
              type="button"
              className="sidebar-toggle-btn"
              onClick={() => {
                if (window.innerWidth < 1024) {
                  setMobileOpen(false);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label="Toggle navigation"
            >
              {sidebarCollapsed ? <ReferenceIcon name="chevron" /> : <ReferenceIcon name="close" />}
            </button>
          </div>

          <nav className="main-nav" aria-label="Main navigation">
            {allowedGroups.map((group) => {
              const isGroupActive = group.items.some((item) => routeMatches(location.pathname, item.to));
              if (!group.collapsible) {
                return (
                  <Fragment key={group.id}>
                    {group.label && <span className="nav-section-label">{group.label}</span>}
                    {group.items.map(({ to, label, icon, hidden }) => (
                      <NavLink
                        key={to}
                        to={to}
                        hidden={hidden}
                        end={to === '/'}
                        aria-current={routeMatches(location.pathname, to) ? 'page' : undefined}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) => `nav-link direct-nav-link ${isActive || routeMatches(location.pathname, to) ? 'active' : ''}`}
                      >
                        <ReferenceIcon name={icon} className="nav-link-icon" />
                        <span className="nav-link-text">{label}</span>
                      </NavLink>
                    ))}
                  </Fragment>
                );
              }
              return (
                <div className={`nav-group ${isGroupActive ? 'has-active-child' : ''}`} key={group.id}>
                  <button
                    type="button"
                    className="nav-group-trigger"
                    aria-expanded={expanded[group.id]}
                    onClick={() => setExpanded((current) => ({ ...current, [group.id]: !current[group.id] }))}
                  >
                    <span>{group.label}</span>
                    <ReferenceIcon name="chevron" className={`group-chevron ${expanded[group.id] ? 'is-open' : ''}`} />
                  </button>
                  {expanded[group.id] && (
                    <div className="nav-group-items">
                      {group.items.map(({ to, label, icon, hidden }) => (
                        <NavLink
                          key={to}
                          to={to}
                          hidden={hidden}
                          end={to === '/'}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        >
                          <ReferenceIcon name={icon} className="nav-link-icon" />
                          <span className="nav-link-text">{label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <nav className="sr-only" aria-label="Additional authorized navigation">
            {allowedUtilityItems
              .filter((item) => !['/profile', '/settings/account', '/settings/security'].includes(item.to))
              .map((item) => <Link key={item.to} to={item.to}>{item.label}</Link>)}
          </nav>

          <div className="sidebar-bottom">
            <NavLink
              to="/settings/account"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `nav-link sidebar-settings-link ${isActive ? 'active' : ''}`}
            >
              <ReferenceIcon name="settings" className="nav-link-icon" />
              <span className="nav-link-text">Settings</span>
            </NavLink>
            <div className="profile-card sidebar-profile-card" title={`Signed in as ${profileDisplayName}`}>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className="sidebar-profile-identity">
                <span className="sidebar-profile-avatar">{initials(profileDisplayName)}</span>
                <span className="sidebar-profile-copy"><strong>{profileDisplayName}</strong><small>{userRole}</small></span>
              </Link>
              <button
                type="button"
                className="sidebar-profile-logout"
                disabled={signingOut}
                onClick={() => void logout()}
                title="Log out"
                aria-label="Log out"
              >
                <ReferenceIcon name="logout" className="logout-icon" />
              </button>
            </div>
          </div>
        </aside>

        {/* Content Frame */}
        <div className="content-frame">
          <main id="main-content">{children}</main>
        </div>
      </div>
    </div>
  );
}

function GlobalSearchOption({ item, index, active, onSelect, onHover }: {
  item: SearchItem;
  index: number;
  active: boolean;
  onSelect: (item: SearchItem) => void;
  onHover: (index: number) => void;
}) {
  return <button
    type="button"
    id={`global-search-result-${index}`}
    className={`global-search-option ${active ? 'active' : ''}`}
    role="option"
    aria-selected={active}
    onMouseEnter={() => onHover(index)}
    onClick={() => onSelect(item)}
  >
    <span className={`global-search-option-icon ${item.source}`}><ReferenceIcon name={item.icon}/></span>
    <span className="global-search-option-copy"><strong>{item.title}</strong><small>{item.subtitle}</small></span>
    {item.status && <span className="global-search-option-status">{item.status.replaceAll('_', ' ')}</span>}
    <ReferenceIcon name="chevron" className="global-search-option-arrow"/>
  </button>;
}

function routeMatches(pathname: string, target: string): boolean {
  if (target === '/uploads' && pathname === '/data-sources') return true;
  return target === '/' ? pathname === '/' : pathname === target || pathname.startsWith(`${target}/`);
}

function initials(value: string) {
  return value.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'U';
}

function friendlyRole(value = 'supervisor') {
  if (value === 'admin') return 'Client';
  if (value === 'superadmin') return 'Superadmin';
  if (value === 'qc') return 'QC Reviewer';
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
